import React, { useState } from 'react';
import Papa from 'papaparse';
import { collection, addDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import ImportSuccessModal from './ImportSuccessModal';
import './AICharacterImport.css';

const AICharacterImport = ({ userId, userData, setId, onComplete }) => {
  const [characters, setCharacters] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!characters.trim()) {
      setError('Please enter some characters');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = await auth.currentUser.getIdToken();

      const response = await fetch('https://backend.hanzi-flashcards.com/api/translation/translate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          characters: characters.trim(),
          userId
        })
      });

      const data = await response.json();
      console.log('[AI Import] raw response data:', data);

      if (!response.ok) {
        throw new Error(data.error || 'Failed to process characters');
      }

      console.log('[AI Import] csv string:', data.csv);

      Papa.parse(data.csv, {
        header: false,
        skipEmptyLines: true,
        delimiter: ",",
        complete: async (results) => {
          console.log('[AI Import] parsed rows:', results.data);
          try {
            const characterData = results.data
              .filter(row => row[0]?.toLowerCase() !== 'character')
              .map(row => ({
                hanzi: row[0],
                pinyin: row[1],
                translation: row[2],
                userId,
                setId
              }))
              .filter(char => char.hanzi && char.pinyin && char.translation);

            const addPromises = characterData.map(char =>
              addDoc(collection(db, 'characters'), char)
            );
            await Promise.all(addPromises);
            setImportedCount(characterData.length);
            setShowSuccess(true);
            await onComplete();
            setCharacters('');

            document.querySelector('.characters-list').scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          } catch (err) {
            console.error('Error processing character data:', err)
            setError('Error processing character data');
          }
        },
        error: (err) => {
          console.error('Error parsing response:', err);
          setError('Error parsing response');
        }
      });
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ai-import">
      <h3>Create from your text</h3>
      <p className="description">
        {userData?.tier === 'free'
          ? 'Paste up to 20 characters and we\'ll get their pinyin and translations'
          : 'Paste up to 50 characters and we\'ll get their pinyin and translations'}
      </p>
      <form onSubmit={handleSubmit}>
        <textarea
          value={characters}
          onChange={(e) => {
            setCharacters(e.target.value);
            const uniqueChineseChars = new Set(e.target.value.match(/[\u4e00-\u9fff]/g) || []).size;
            setCharCount(uniqueChineseChars);
          }}
          placeholder="Paste Chinese characters here"
          rows={4}
        />
        <div className="char-counter">
          {charCount}/{userData?.tier === 'free' ? '20' : '50'} unique Hanzi characters
        </div>
        {error && <div className="error">{error}</div>}
        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading || !characters.trim()}
        >
          {loading ? 'Processing...' : 'Import Characters'}
        </button>
      </form>
      {showSuccess && (
        <ImportSuccessModal
          count={importedCount}
          onClose={() => setShowSuccess(false)}
        />
      )}
    </div>
  );
};

export default AICharacterImport;